/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import {
    createContext,
    useState,
    useRef,
    useMemo,
    useCallback,
    useEffect,
    useContext,
} from 'react';
import useInterval from 'use-interval';
import { useAuth0 } from '@auth0/auth0-react';

import Config from 'Config';
import { useAuth0Config } from 'Auth0ProviderWithConfig';
import {
    configureTokenRefresh,
    configureLocalTokenRefresh,
    refreshS2Token,
    extendS2Token,
    getToken,
    logout,
} from 'common/services/AuthService.js';
import { getExpireIn, hasSessionToken } from 'common/services/LogoutService';
import { useIdle } from 'common/utils/hooks/useIdle';
import { BroadcastChannel, createLeaderElection } from 'broadcast-channel';
import * as logger from 'common/utils/Logger';
import { useAppDispatch } from 'common/stores/redux/hooks';
import {
    setAuthentication,
    updateToken,
} from 'common/stores/redux/Authentication';

const idleTimeoutMs = Config.tokenValidation.inactivityTimeout * 60 * 1000;

export const UserTokenContext = createContext({});

export const UserTokenProvider = ({ children }) => {
    const idleAt = useRef(null);
    const isMounted = useRef(true);

    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const { isLocal } = useAuth0Config();
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isLocal) {
            configureLocalTokenRefresh();
            return;
        }
        configureTokenRefresh(getAccessTokenSilently);
    }, [isLocal, getAccessTokenSilently]);

    const [idle, resetIdle] = useIdle(idleTimeoutMs);

    const defaultIntervalCheck = 60000; // 1 minute in milliseconds
    const intervalCheckWhenDialogShow = 1000; // 1 second in milliseconds
    const timeBeforeExpiredInSeconds =
        Config.tokenValidation.timeBeforeExpired * 60;
    const [showTimeoutDialog, setShowTimeoutDialog] = useState(false);
    const [expiredIn, setExpiredIn] = useState(0);
    const [intervalCheckToken, setIntervalCheckToken] =
        useState(defaultIntervalCheck);
    const [isLeader, setIsLeader] = useState(false);

    const motiliAppChannel = useMemo(() => {
        return new BroadcastChannel('motili-app-channel', {
            type: 'localstorage',
            removeOnClose: true,
        });
    }, []);

    const broadcastMotiliAppMessage = useCallback(
        async eventMessage => {
            if (motiliAppChannel) {
                await motiliAppChannel.postMessage(eventMessage);
            }
        },
        [motiliAppChannel]
    );

    /**
     * Leader elector to manage which tab handles token refresh
     */
    const leaderElector = useMemo(() => {
        return createLeaderElection(motiliAppChannel);
    }, [motiliAppChannel]);

    const resetIdleCountdown = useCallback(() => {
        idleAt.current = null;
        setIntervalCheckToken(defaultIntervalCheck);
        setShowTimeoutDialog(false);
        setExpiredIn(0);

        resetIdle(); // reset idle state in useIdle hook
    }, [resetIdle]);

    const startIdleCountdown = useCallback(() => {
        setExpiredIn(timeBeforeExpiredInSeconds);
        setIntervalCheckToken(intervalCheckWhenDialogShow);
        setShowTimeoutDialog(true);
        idleAt.current = Date.now();
    }, [timeBeforeExpiredInSeconds]);

    const tickIdleCountdown = useCallback(async () => {
        const elapsed = Math.floor((Date.now() - idleAt.current) / 1000);
        const remaining = timeBeforeExpiredInSeconds - elapsed;

        if (remaining <= 0) {
            logger.info('session expired (idle timeout), logging out');
            resetIdleCountdown();
            logout();
            await broadcastMotiliAppMessage({
                type: 'USER_SESSION',
                loggedIn: false,
            });
        } else {
            setExpiredIn(remaining);
        }
    }, [
        broadcastMotiliAppMessage,
        timeBeforeExpiredInSeconds,
        resetIdleCountdown,
    ]);

    const checkTokenExpiry = useCallback(async () => {
        if (getExpireIn() > 0) {
            return;
        }
        logger.info('session expired, logging out');
        logout();
        await broadcastMotiliAppMessage({
            type: 'USER_SESSION',
            loggedIn: false,
        });
    }, [broadcastMotiliAppMessage]);

    const renewTokenIfExpiring = useCallback(
        async renew => {
            if (getExpireIn() > timeBeforeExpiredInSeconds) {
                return;
            }

            const updatedToken = await renew();

            if (updatedToken) {
                dispatch(updateToken(updatedToken));
            }
        },
        [timeBeforeExpiredInSeconds, dispatch]
    );

    const calculateSessionTimeout = useCallback(async () => {
        try {
            // Always refresh token if leader, even during idle,
            // to prevent stale token when user returns from another window
            if (isLeader) {
                await renewTokenIfExpiring(refreshS2Token);
            }

            if (idle && !showTimeoutDialog) {
                startIdleCountdown();
                return;
            }
            if (showTimeoutDialog && idleAt.current) {
                await tickIdleCountdown();
                return;
            }
            await checkTokenExpiry();
        } catch (err) {
            // don't log this in sentry! Use console.error so we don't flood sentry with token errors
            console.error('validate token error', err);
        }
    }, [
        showTimeoutDialog,
        startIdleCountdown,
        tickIdleCountdown,
        checkTokenExpiry,
        idle,
        isLeader,
        renewTokenIfExpiring,
    ]);

    const extendUserToken = useCallback(
        async force => {
            try {
                if (force) {
                    resetIdleCountdown();

                    await broadcastMotiliAppMessage({
                        type: 'USER_SESSION_TIMEOUT',
                        showTimeoutDialog: false,
                    });

                    const updatedToken = await extendS2Token(true);

                    if (updatedToken) {
                        dispatch(updateToken(updatedToken));
                    }

                    return;
                }

                await renewTokenIfExpiring(extendS2Token);

                resetIdleCountdown();

                await broadcastMotiliAppMessage({
                    type: 'USER_SESSION_TIMEOUT',
                    showTimeoutDialog: false,
                });
            } catch (err) {
                // don't log this in sentry! Use console.error so we don't flood sentry with token errors
                console.error('Failed to extend session token', err);
            }
        },
        [
            broadcastMotiliAppMessage,
            resetIdleCountdown,
            renewTokenIfExpiring,
            dispatch,
        ]
    );

    /**
     * Extend token when web reload or first hit url
     */
    useEffect(() => {
        (async () => {
            if (isLeader) {
                await extendUserToken();
            }
        })();
    }, [extendUserToken, isLeader]);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    /**
     * Broadcast message for timeout dialog status
     */
    useEffect(() => {
        (async () => {
            if (showTimeoutDialog) {
                await broadcastMotiliAppMessage({
                    type: 'USER_SESSION_TIMEOUT',
                    showTimeoutDialog,
                    idleAt: idleAt.current,
                });
            }
        })();
    }, [showTimeoutDialog, broadcastMotiliAppMessage]);

    /**
     * Handle broadcast massage
     */
    useEffect(() => {
        (async () => {
            // Handle on message broadcast
            if (motiliAppChannel) {
                motiliAppChannel.onmessage = async message => {
                    try {
                        if (message?.type === 'USER_SESSION') {
                            if (message?.loggedIn) {
                                if (!isAuthenticated) {
                                    logger.info(
                                        'another tab logged in, refreshing token'
                                    );
                                    await getAccessTokenSilently();
                                }
                                await calculateSessionTimeout();
                                const token = getToken();

                                if (token) {
                                    dispatch(setAuthentication(token));
                                } else {
                                    logger.info(
                                        'no session token, logging out'
                                    );
                                    resetIdleCountdown();
                                    logout({ isAuthenticated });
                                }
                            } else {
                                logger.info('not logged in, logging out');
                                resetIdleCountdown();
                                logout({ isAuthenticated });
                            }
                        } else if (message?.type === 'USER_SESSION_TIMEOUT') {
                            if (isMounted.current) {
                                setShowTimeoutDialog(
                                    message?.showTimeoutDialog
                                );

                                if (message?.showTimeoutDialog) {
                                    idleAt.current = message.idleAt ?? null;
                                    setIntervalCheckToken(
                                        intervalCheckWhenDialogShow
                                    );
                                } else {
                                    resetIdleCountdown();
                                }
                            }
                        }
                    } catch (error) {
                        // don't log this in sentry. use console.error
                        console.error(
                            'Error during processing broadcast message',
                            {
                                message,
                                error,
                            }
                        );
                    }
                };
            }
        })();
    }, [
        dispatch,
        motiliAppChannel,
        calculateSessionTimeout,
        isAuthenticated,
        getAccessTokenSilently,
        resetIdleCountdown,
    ]);

    useEffect(() => {
        (async () => {
            try {
                await leaderElector.awaitLeadership();
                setIsLeader(true);
            } catch (error) {
                logger.error('Failed to set tab leader for broadcast channel', {
                    error,
                });
            }
        })();
        return () => {
            (async () => {
                await leaderElector.die();
            })();
        };
    }, [leaderElector]);

    /**
     * Interval for controlling session timeout dialog
     * - If user is idle, show timeout dialog and start countdown to logout
     * - If user is not idle, log out if the token has expired
     */
    useInterval(
        async () => {
            if (hasSessionToken()) {
                await calculateSessionTimeout();
            } else {
                if (isMounted.current) {
                    setIntervalCheckToken(null);
                }
                if (!isAuthenticated) {
                    await broadcastMotiliAppMessage({
                        type: 'USER_SESSION',
                        loggedIn: false,
                    });
                }
            }
        },
        intervalCheckToken,
        true
    );

    const value = useMemo(
        () => ({
            showTimeoutDialog,
            expiredIn,
            extendToken: () => extendUserToken(true),
            setLoggedIn: async loggedIn => {
                if (loggedIn) {
                    await calculateSessionTimeout();
                }
                await broadcastMotiliAppMessage({
                    type: 'USER_SESSION',
                    loggedIn,
                });
            },
        }),
        [
            showTimeoutDialog,
            expiredIn,
            broadcastMotiliAppMessage,
            calculateSessionTimeout,
            extendUserToken,
        ]
    );

    return (
        <UserTokenContext.Provider value={value}>
            {children}
        </UserTokenContext.Provider>
    );
};

export const useUserToken = () => {
    return useContext(UserTokenContext);
};
